/**
 * ModelCard Component
 * Displays a base model option for LoRA training with specs and selection state
 */

import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ModelInfo } from "../types";

interface ModelCardProps {
  model: ModelInfo;
  isSelected: boolean;
  onSelect: () => void;
}

export function ModelCard({ model, isSelected, onSelect }: ModelCardProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "relative text-left p-5 rounded-lg border transition-all duration-200",
        "hover:bg-primary/5",
        isSelected
          ? "border-primary bg-primary/5"
          : "border-white/10 bg-[#1a1a1a]",
      )}
    >
      {/* Recommended badge */}
      {model.isRecommended && (
        <div className="absolute -top-2.5 left-4 px-2 py-0.5 rounded bg-primary text-[10px] font-semibold tracking-wider">
          RECOMMENDED
        </div>
      )}

      {/* Selection indicator */}
      {isSelected && (
        <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
          <Check className="w-3 h-3 text-white" />
        </div>
      )}

      {/* Header */}
      <div className="mb-4">
        <h3 className="text-base font-medium text-foreground">{model.name}</h3>
        <p className="text-xs text-muted-foreground mt-0.5">{model.vendor}</p>
      </div>

      {/* Spec bars */}
      <div className="space-y-2 mb-4">
        <SpecBar label="Fidelity" value={model.fidelity} />
        <SpecBar label="Speed" value={model.speed} />
        <SpecBar label="VRAM" value={model.vram} />
      </div>

      {/* Bullets */}
      <ul className="space-y-1.5 mb-4">
        {model.bullets.map((bullet, i) => (
          <li
            key={i}
            className="text-xs text-muted-foreground flex items-start gap-2"
          >
            <span className="text-primary mt-1.5 w-1 h-1 rounded-full bg-current flex-shrink-0" />
            {bullet}
          </li>
        ))}
      </ul>

      {/* License */}
      <div className="pt-3 border-t border-white/5">
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
          {model.license}
        </span>
      </div>
    </button>
  );
}

// Spec bar component - supports empty, half, and full bars
interface SpecBarProps {
  label: string;
  value: number; // 1-5, supports half values (e.g., 4.5)
}

function SpecBar({ label, value }: SpecBarProps) {
  // Determine bar state: 'full' | 'half' | 'empty'
  const getBarState = (index: number): "full" | "half" | "empty" => {
    const threshold = index + 1; // Bar 0 = threshold 1, Bar 4 = threshold 5
    if (value >= threshold) return "full";
    if (value >= threshold - 0.5) return "half";
    return "empty";
  };

  return (
    <div className="flex items-center gap-3">
      <span className="text-[10px] text-muted-foreground uppercase tracking-wider w-14">
        {label}
      </span>
      <div className="flex gap-0.5">
        {Array.from({ length: 5 }, (_, i) => {
          const state = getBarState(i);
          return (
            <div
              key={i}
              className="w-4 h-1.5 rounded-sm bg-white/10 overflow-hidden"
            >
              {state !== "empty" && (
                <div
                  className={cn(
                    "h-full bg-primary transition-all",
                    state === "full" ? "w-full" : "w-1/2",
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
